import React, { createContext, useContext, useState, useCallback } from 'react';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  actions?: NotificationAction[];
}

export interface NotificationAction {
  label: string;
  onClick: () => void;
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: ((notification: Omit<Notification, 'id'>) => void) & ((title: string, type: NotificationType, message?: string) => void);
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((
    notificationOrTitle: Omit<Notification, 'id'> | string,
    type?: NotificationType,
    message?: string
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    
    let newNotification: Notification;
    
    // Handle both formats: object format and legacy string format
    if (typeof notificationOrTitle === 'string') {
      // Legacy format: showNotification(title, type, message?)
      newNotification = {
        id,
        title: notificationOrTitle,
        type: type!,
        message,
        duration: 5000
      };
    } else {
      // New format: showNotification({title, type, message?, ...})
      newNotification = {
        ...notificationOrTitle,
        id,
        duration: notificationOrTitle.duration || 5000
      };
    }
    
    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      showNotification,
      removeNotification,
      clearNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};